import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { categories, Tool } from '../data/tools';
import { Button } from './ui/Button';
import { useLanguageRouter } from '../utils/i18nRouter';

interface SidebarProps {
  className?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set() // 默认不展开任何分类
  );

  // 从URL获取当前选中的工具
  const getCurrentTool = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts.length >= 4 && pathParts[1] === 'tools') {
      const categoryId = pathParts[2];
      const toolId = pathParts[3];
      const category = categories.find(cat => cat.id === categoryId);
      return category?.tools.find(tool => tool.id === toolId) || null;
    }
    return null;
  };

  const selectedTool = getCurrentTool();

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const colorClasses = {
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400',
    purple: 'text-purple-600 dark:text-purple-400',
    orange: 'text-orange-600 dark:text-orange-400',
    pink: 'text-pink-600 dark:text-pink-400',
    indigo: 'text-indigo-600 dark:text-indigo-400',
    teal: 'text-teal-600 dark:text-teal-400',
    cyan: 'text-cyan-600 dark:text-cyan-400',
    red: 'text-red-600 dark:text-red-400',
  };

  const handleToolSelect = (tool: Tool) => {
    navigate(getLocalizedPath(tool.path));
  };

  return (
    <div className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full flex flex-col ${className}`}>
      {/* Logo and Title */}
      <div className="p-6 border-gray-200 dark:border-gray-700">
        <Link to={getLocalizedPath('/')} className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-white font-bold text-lg">TC</span>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t('header.title')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('header.subtitle')}
            </p>
          </div>
        </Link>
      </div>

      {/* Categories */}
      <div className="flex-1 overflow-y-auto p-4">
        
        <nav className="space-y-2">
          {categories.map((category) => {
            const IconComponent = category.icon;
            const isExpanded = expandedCategories.has(category.id);
            const colorClass = colorClasses[category.color as keyof typeof colorClasses];
            
            return (
              <div key={category.id}>
                {/* Category Header */}
                <Button
                  variant="ghost"
                  className="w-full justify-start p-2 h-auto text-left"
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={isExpanded}
                  aria-controls={`category-${category.id}-tools`}
                >
                  <div className="flex items-center w-full">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 mr-3 ${colorClass}`}>
                      <IconComponent className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {t(`categories.${category.id}`)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {t('home.toolsCount', { count: category.tools.length })}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </Button>
                
                {/* Tools List */}
                {isExpanded && (
                  <div
                    id={`category-${category.id}-tools`}
                    className="ml-11 mt-2 space-y-1"
                    role="group"
                    aria-labelledby={`category-${category.id}-header`}
                  >
                    {category.tools.map((tool) => {
                      const ToolIcon = tool.icon;
                      const isSelected = selectedTool?.id === tool.id;
                      
                      return (
                        <Button
                          key={tool.id}
                          variant={isSelected ? "secondary" : "ghost"}
                          className="w-full justify-start p-2 h-auto text-left"
                          onClick={() => handleToolSelect(tool)}
                          aria-pressed={isSelected}
                          aria-describedby={`tool-${tool.id}-description`}
                        >
                          <div className="flex items-center w-full">
                            <ToolIcon className="w-4 h-4 text-gray-500 dark:text-gray-400 mr-3 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900 dark:text-white truncate">
                                {t(tool.name)}
                              </div>
                              {tool.featured && (
                                <div className="flex items-center mt-1">
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                                    {t('home.featured')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </Button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
